import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Divider,
    IconButton, Tooltip, Typography
} from '@mui/material';
import {
    ArrowBack, CheckCircle, HourglassEmpty,
    PlayArrow, RadioButtonUnchecked, Send, Timer, Undo
} from '@mui/icons-material';
import { useKDSEstacao } from '../../hooks/useKDSEstacao';
import { useKDSExpedicaoArea } from '../../hooks/useKDSExpedicaoArea';
import { useEstacoesKDS } from '../../hooks/useEstacoesKDS';
import { KDSCard } from '../../components/KDSCard';
import { IItemPedidoKDS, IPedidoKDSExpedicao, StatusItemKDS, StatusKDS } from '../../interfaces/IKDS';
import { KDSTimer } from '../../components/KDSTimer';
import { useKDSKeyboardNavigation } from '../../hooks/useKDSKeyboardNavigation';
import { useKdsTokens } from '../../theme/kdsTokens';

const calcularStatusKDS = (itens: IItemPedidoKDS[]): StatusKDS => {
    const ativos = itens.filter(i => i.status !== StatusItemKDS.CANCELADO && i.status !== StatusItemKDS.LIBERADO);
    if (!ativos.length) return StatusKDS.PARADO;
    if (ativos.every(i => i.status === StatusItemKDS.PRONTO)) return StatusKDS.CONCLUIDO;
    if (ativos.some(i => i.status === StatusItemKDS.EM_PREPARO)) return StatusKDS.EM_ANDAMENTO;
    return StatusKDS.PARADO;
};

// ─── Painel Estação Normal ────────────────────────────────────────────────────

const PainelEstacaoNormal: React.FC<{
    estacaoUuid: string;
    nomeEstacao: string;
    corEstacao: string;
    tempoAlertaAmarelo: number;
    tempoAlertaVermelho: number;
}> = ({ estacaoUuid, nomeEstacao, corEstacao, tempoAlertaAmarelo, tempoAlertaVermelho }) => {
    const navigate = useNavigate();
    const tokens = useKdsTokens();
    const { itens, loading, iniciarItem, concluirItem, concluirTodosItensPedido, reverterItem, liberarPedido } = useKDSEstacao(estacaoUuid);

    // Uma conta de mesa recebe vários envios pelo QR Code: cada rodada vira o seu card,
    // senão a segunda rodada reabriria o card da primeira, que já estava pronto.
    const pedidosAgrupados = useMemo(() => {
        const mapa = new Map<string, IItemPedidoKDS[]>();
        for (const item of itens) {
            const chave = item.rodadaUuid ? `${item.pedidoUuid}:${item.rodadaUuid}` : item.pedidoUuid;
            if (!mapa.has(chave)) mapa.set(chave, []);
            mapa.get(chave)!.push(item);
        }
        return Array.from(mapa.entries())
            .map(([chave, items]) => ({
                chave,
                pedidoUuid: items[0].pedidoUuid,
                rodadaNumero: items[0].rodadaNumero,
                itens: items,
                todosProntos: items.every(i => i.status === StatusItemKDS.PRONTO),
                statusKDS: calcularStatusKDS(items),
            }))
            .sort((a, b) => {
                if (a.todosProntos && !b.todosProntos) return 1;
                if (!a.todosProntos && b.todosProntos) return -1;
                return new Date(a.itens[0].criadoEm).getTime() - new Date(b.itens[0].criadoEm).getTime();
            });
    }, [itens]);

    const navItems = useMemo(() => pedidosAgrupados.map(p => ({ pedidoUuid: p.pedidoUuid, itens: p.itens })), [pedidosAgrupados]);
    const { selectedPedidoUuid, selectedItemUuid } = useKDSKeyboardNavigation(navItems, {
        onIniciarItem: iniciarItem,
        onConcluirItem: concluirItem,
        onReverterItem: reverterItem,
        onConcluirTodos: concluirTodosItensPedido,
        onLiberarPedido: liberarPedido,
    });

    return (
        <Box sx={{ width: '100vw', height: '100vh', overflow: 'hidden', background: tokens.painel, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ background: corEstacao, px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Voltar para configurações">
                        <IconButton size="small" onClick={() => navigate('/cozinha/configuracao')} sx={{ color: 'white' }}>
                            <ArrowBack fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="h5" fontWeight="bold" color="white">{nomeEstacao}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <Typography color="white" fontSize="0.9rem">
                        {pedidosAgrupados.filter(p => !p.todosProntos).length} em preparo
                    </Typography>
                    <Typography color="white" fontSize="0.9rem">
                        {pedidosAgrupados.filter(p => p.todosProntos).length} prontos
                    </Typography>
                    {loading && <CircularProgress size={20} sx={{ color: 'white' }} />}
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 2, alignContent: 'flex-start', alignItems: 'start' }}>
                {pedidosAgrupados.length === 0 && !loading && (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                        <Typography color="text.secondary" variant="h5">Nenhum pedido no momento</Typography>
                    </Box>
                )}
                {pedidosAgrupados.map(pedido => (
                    <KDSCard
                        key={pedido.chave}
                        pedidoUuid={pedido.pedidoUuid}
                        numeroPedido={pedido.itens[0]?.numeroPedido}
                        rodadaNumero={pedido.rodadaNumero}
                        itens={pedido.itens}
                        configuracaoExibicao={{ tempoAlertaAmareloSegundos: tempoAlertaAmarelo, tempoAlertaVermelhoSegundos: tempoAlertaVermelho }}
                        onIniciarItem={iniciarItem}
                        onConcluirItem={concluirItem}
                        // "Concluir todos" do servidor vale para o pedido inteiro na estação; num card
                        // de rodada só os itens daquela rodada podem ser concluídos.
                        onConcluirTodos={pedido.rodadaNumero
                            ? () => pedido.itens
                                // Mesmos status que o servidor conclui em lote.
                                .filter(item => item.status === StatusItemKDS.AGUARDANDO || item.status === StatusItemKDS.EM_PREPARO)
                                .forEach(item => concluirItem(item.uuid))
                            : concluirTodosItensPedido}
                        onReverterItem={reverterItem}
                        isSelected={pedido.pedidoUuid === selectedPedidoUuid}
                        selectedItemUuid={selectedItemUuid}
                        statusKDS={pedido.statusKDS}
                    />
                ))}
            </Box>
        </Box>
    );
};

// ─── Painel Expedição por Área ────────────────────────────────────────────────

const IconeStatus: React.FC<{ status: StatusItemKDS; size?: number }> = ({ status, size = 18 }) => {
    const tokens = useKdsTokens();
    const sx = { fontSize: size, color: tokens.statusCor(status) };
    if (status === StatusItemKDS.PRONTO) return <CheckCircle sx={sx} />;
    if (status === StatusItemKDS.EM_PREPARO) return <HourglassEmpty sx={sx} />;
    if (status === StatusItemKDS.AGUARDANDO_DISPARO) return <Timer sx={sx} />;
    return <RadioButtonUnchecked sx={sx} />;
};

const PainelExpedicaoArea: React.FC<{
    estacaoUuid: string;
    nomeEstacao: string;
    corEstacao: string;
    tempoAlertaAmarelo: number;
    tempoAlertaVermelho: number;
}> = ({ estacaoUuid, nomeEstacao, corEstacao, tempoAlertaAmarelo, tempoAlertaVermelho }) => {
    const navigate = useNavigate();
    const tokens = useKdsTokens();
    const {
        pedidos, loading,
        iniciarItem, concluirItem, concluirTodosItensPedido,
        liberarPedido, dispararPedido, dispararItemIndividual, reverterItem,
    } = useKDSExpedicaoArea(estacaoUuid);
    const { estacoes } = useEstacoesKDS();

    const nomeEstacaoFn = (uuid: string) => estacoes.find(e => e.uuid === uuid)?.nome ?? uuid.slice(-4);
    const corEstacaoFn = (uuid: string) => estacoes.find(e => e.uuid === uuid)?.cor ?? tokens.textoFraco;
    const formatMin = (s?: number) => s ? `${Math.round(s / 60)} min` : null;

    const porOrdemChegada = (lista: IPedidoKDSExpedicao[]) =>
        [...lista].sort((a, b) =>
            a.criadoEm && b.criadoEm
                ? new Date(a.criadoEm as any).getTime() - new Date(b.criadoEm as any).getTime()
                : 0
        );

    const aguardandoDisparo = porOrdemChegada(pedidos.filter(p => p.temDisparo));
    const emAndamento = porOrdemChegada(pedidos.filter(p => !p.temDisparo && !p.emDisparo && !p.todosProntos));
    const emDisparo = porOrdemChegada(pedidos.filter(p => p.emDisparo && !p.temDisparo && !p.todosProntos));
    const prontos = porOrdemChegada(pedidos.filter(p => p.todosProntos));

    const renderItemDisparo = (item: IItemPedidoKDS) => {
        const jaDisparado = item.status !== StatusItemKDS.AGUARDANDO_DISPARO || !!item.disparadoEm;
        return (
            <Box key={item.uuid} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1,
                borderRadius: 1,
                background: tokens.tenue(jaDisparado ? tokens.palette.success.main : tokens.palette.warning.main, 0.1),
                border: `1px solid ${tokens.tenue(jaDisparado ? tokens.palette.success.main : tokens.palette.warning.main, 0.5)}`,
            }}>
                <Chip label={nomeEstacaoFn(item.estacaoKdsUuid)} size="small" sx={{ background: corEstacaoFn(item.estacaoKdsUuid), color: 'white', fontSize: '0.7rem', minWidth: 70, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize="0.875rem" fontWeight={600} noWrap sx={{ color: tokens.texto }}>
                        {item.quantidade}x {item.produtoDescricao}
                    </Typography>
                    {item.observacoes && <Typography fontSize="0.72rem" noWrap sx={{ color: tokens.palette.warning.main, fontWeight: 600 }}>⚠ {item.observacoes}</Typography>}
                </Box>
                {item.tempoEstimadoSegundos > 0 && (
                    <Typography fontSize="0.78rem" sx={{ flexShrink: 0, color: tokens.textoSecundario }}>{formatMin(item.tempoEstimadoSegundos)}</Typography>
                )}
                {!jaDisparado ? (
                    <Button variant="contained" color="warning" size="small" startIcon={<PlayArrow sx={{ fontSize: 14 }} />}
                        onClick={() => dispararItemIndividual(item.uuid)}
                        sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '0.75rem', py: 0.5, px: 1, flexShrink: 0 }}
                    >
                        Disparar
                    </Button>
                ) : (
                    <Chip
                        label="Disparado"
                        size="small"
                        icon={<CheckCircle sx={{ fontSize: '14px !important', color: `${tokens.palette.success.main} !important` }} />}
                        sx={{ background: tokens.tenue(tokens.palette.success.main, 0.14), color: tokens.palette.success.main, fontSize: '0.7rem', flexShrink: 0 }}
                    />
                )}
            </Box>
        );
    };

    const renderPedido = (pedido: IPedidoKDSExpedicao, showDispatch: boolean) => {
        const numeroPedido = pedido.numeroPedido ?? pedido.itens[0]?.numeroPedido;
        const primeiroItem = pedido.itens[0];
        const itensDisparar = pedido.itens.filter(i => i.status === StatusItemKDS.AGUARDANDO_DISPARO);
        const itensAtivos = pedido.itens.filter(i =>
            (i.status !== StatusItemKDS.AGUARDANDO_DISPARO || !showDispatch) &&
            i.status !== StatusItemKDS.CANCELADO &&
            i.status !== StatusItemKDS.LIBERADO
        );

        // Grupos por produto para exibição agrupada
        const gruposMapa = new Map<string, { produtoDescricao: string; quantidade: number; itens: IItemPedidoKDS[] }>();
        for (const item of itensAtivos) {
            if (!gruposMapa.has(item.produtoUuid)) {
                gruposMapa.set(item.produtoUuid, { produtoDescricao: item.produtoDescricao, quantidade: item.quantidade, itens: [] });
            }
            const g = gruposMapa.get(item.produtoUuid)!;
            if (item.itemTipo === 'produto' && item.quantidade > 1) g.quantidade = item.quantidade;
            g.itens.push(item);
        }
        const grupos = Array.from(gruposMapa.values());
        const agrupado = grupos.some(g => g.itens.length > 1) || itensAtivos.some(i => !!i.processoDescricao);

        return (
            <Box key={pedido.pedidoUuid} sx={{
                background: tokens.card,
                border: `1px solid ${tokens.borda}`,
                borderLeft: `3px solid ${pedido.todosProntos
                    ? tokens.palette.success.main
                    : pedido.temDisparo
                        ? tokens.palette.warning.main
                        : pedido.emDisparo
                            ? tokens.palette.primary.main
                            : tokens.textoFraco}`,
                borderRadius: 2,
                overflow: 'hidden',
            }}>
                {/* Cabeçalho */}
                <Box sx={{
                    px: 2, py: 1,
                    background: pedido.todosProntos
                        ? tokens.tenue(tokens.palette.success.main, 0.1)
                        : pedido.temDisparo
                            ? tokens.tenue(tokens.palette.warning.main, 0.1)
                            : pedido.emDisparo
                                ? tokens.tenue(tokens.palette.primary.main, 0.1)
                                : tokens.superficie,
                    borderBottom: `1px solid ${tokens.borda}`,
                    display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                }}>
                    <Typography fontWeight={800} fontSize="1rem" sx={{ color: tokens.texto }}>
                        #{numeroPedido ?? pedido.pedidoUuid.slice(-6).toUpperCase()}
                    </Typography>
                    {primeiroItem && (
                        <KDSTimer
                            criadoEm={primeiroItem.criadoEm}
                            iniciadoEm={primeiroItem.iniciadoEm}
                            tempoEstimadoSegundos={pedido.tempoEstimadoTotalSegundos ?? 0}
                            tempoAlertaAmareloSegundos={tempoAlertaAmarelo}
                            tempoAlertaVermelhoSegundos={tempoAlertaVermelho}
                        />
                    )}
                    {pedido.tempoEstimadoTotalSegundos != null && pedido.tempoEstimadoTotalSegundos > 0 && (
                        <Chip label={`${formatMin(pedido.tempoEstimadoTotalSegundos)} estimado`} size="small" sx={{ background: tokens.tenue(tokens.palette.primary.main, 0.12), color: tokens.palette.primary.main, fontSize: '0.72rem' }} />
                    )}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        {showDispatch && itensDisparar.length > 1 && (
                            <Button variant="outlined" color="warning" size="small" startIcon={<PlayArrow sx={{ fontSize: 14 }} />}
                                onClick={() => dispararPedido(pedido.pedidoUuid)}
                                sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                            >
                                Disparar todos
                            </Button>
                        )}
                        {pedido.todosProntos && (
                            <Button variant="contained" color="primary" size="small" startIcon={<Send sx={{ fontSize: 14 }} />}
                                onClick={() => liberarPedido(pedido.pedidoUuid)}
                                sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '0.75rem' }}
                            >
                                Liberar
                            </Button>
                        )}
                        {!pedido.todosProntos && !pedido.temDisparo && (
                            <Button variant="contained" color="success" size="small" startIcon={<CheckCircle sx={{ fontSize: 14 }} />}
                                onClick={() => concluirTodosItensPedido(pedido.pedidoUuid)}
                                sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '0.75rem' }}
                            >
                                Tudo pronto
                            </Button>
                        )}
                    </Box>
                </Box>

                <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {/* Itens aguardando disparo */}
                    {showDispatch && itensDisparar.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="caption" fontWeight="bold" sx={{ letterSpacing: 0.5, color: tokens.palette.warning.main }}>
                                AGUARDANDO DISPARO
                            </Typography>
                            {itensDisparar.map(item => renderItemDisparo(item))}
                        </Box>
                    )}

                    {/* Itens ativos com ações iniciar/concluir */}
                    {itensAtivos.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {showDispatch && itensDisparar.length > 0 && <Divider sx={{ borderColor: tokens.borda, my: 0.25 }} />}
                            {agrupado ? grupos.map((grupo, gi) => (
                                <Box key={gi}>
                                    <Typography fontSize="0.8rem" fontWeight={700} sx={{ mb: 0.25, color: tokens.textoSecundario }}>
                                        {grupo.quantidade}x {grupo.produtoDescricao}
                                    </Typography>
                                    {grupo.itens.map(item => {
                                        const label = item.processoDescricao ?? item.itemDescricao ?? item.produtoDescricao;
                                        const ehDestaEstacao = item.estacaoKdsUuid === estacaoUuid;
                                        return (
                                            <Box key={item.uuid} sx={{
                                                display: 'flex', alignItems: 'center', gap: 1, pl: 1,
                                                borderRadius: 1, py: 0.25,
                                                ...(ehDestaEstacao && {
                                                    background: tokens.superficie,
                                                    borderLeft: `3px solid ${corEstacao}`,
                                                    pl: 1.25,
                                                }),
                                            }}>
                                                <IconeStatus status={item.status} size={ehDestaEstacao ? 20 : 16} />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        fontSize={ehDestaEstacao ? '0.875rem' : '0.8rem'}
                                                        fontWeight={ehDestaEstacao ? 700 : 400}
                                                        noWrap
                                                        sx={{ color: ehDestaEstacao ? tokens.texto : tokens.textoFraco }}
                                                    >
                                                        ↳ {item.quantidade}x {label}
                                                    </Typography>
                                                    {item.observacoes && <Typography fontSize="0.75rem" sx={{ color: tokens.palette.warning.main, fontWeight: 600 }}>⚠ {item.observacoes}</Typography>}
                                                </Box>
                                                {!ehDestaEstacao && (
                                                    <Chip label={nomeEstacaoFn(item.estacaoKdsUuid)} size="small" sx={{ background: corEstacaoFn(item.estacaoKdsUuid), color: 'white', fontSize: '0.68rem', height: 20, flexShrink: 0, opacity: 0.7 }} />
                                                )}
                                                {ehDestaEstacao && item.status === StatusItemKDS.AGUARDANDO && (
                                                    <IconButton size="small" onClick={() => iniciarItem(item.uuid)} sx={{ color: tokens.palette.primary.main, p: 0.25 }}>
                                                        <PlayArrow fontSize="small" />
                                                    </IconButton>
                                                )}
                                                {ehDestaEstacao && item.status === StatusItemKDS.EM_PREPARO && (
                                                    <>
                                                        <Tooltip title="Desfazer">
                                                            <IconButton size="small" onClick={() => reverterItem(item.uuid)} sx={{ color: tokens.textoFraco, p: 0.25 }}>
                                                                <Undo sx={{ fontSize: '0.9rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <IconButton size="small" onClick={() => concluirItem(item.uuid)} sx={{ color: tokens.palette.success.main, p: 0.25 }}>
                                                            <CheckCircle fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )}
                                                {ehDestaEstacao && item.status === StatusItemKDS.PRONTO && (
                                                    <Tooltip title="Desfazer">
                                                        <IconButton size="small" onClick={() => reverterItem(item.uuid)} sx={{ color: tokens.palette.primary.main, p: 0.25 }}>
                                                            <Undo sx={{ fontSize: '0.9rem' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        );
                                    })}
                                    {gi < grupos.length - 1 && <Divider sx={{ borderColor: tokens.borda, mt: 0.5 }} />}
                                </Box>
                            )) : itensAtivos.map(item => {
                                const ehDestaEstacao = item.estacaoKdsUuid === estacaoUuid;
                                return (
                                <Box key={item.uuid} sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    borderRadius: 1, py: 0.25,
                                    ...(ehDestaEstacao && {
                                        background: tokens.superficie,
                                        borderLeft: `3px solid ${corEstacao}`,
                                        pl: 1,
                                    }),
                                }}>
                                    <IconeStatus status={item.status} size={ehDestaEstacao ? 20 : 16} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            fontSize={ehDestaEstacao ? '0.875rem' : '0.8rem'}
                                            fontWeight={ehDestaEstacao ? 700 : 400}
                                            noWrap
                                            sx={{ color: ehDestaEstacao ? tokens.texto : tokens.textoFraco }}
                                        >
                                            {item.quantidade}x {item.produtoDescricao}
                                        </Typography>
                                        {item.observacoes && <Typography fontSize="0.75rem" sx={{ color: tokens.palette.warning.main, fontWeight: 600 }}>⚠ {item.observacoes}</Typography>}
                                    </Box>
                                    {!ehDestaEstacao && (
                                        <Chip label={nomeEstacaoFn(item.estacaoKdsUuid)} size="small" sx={{ background: corEstacaoFn(item.estacaoKdsUuid), color: 'white', fontSize: '0.68rem', height: 20, flexShrink: 0, opacity: 0.7 }} />
                                    )}
                                    {ehDestaEstacao && item.status === StatusItemKDS.AGUARDANDO && (
                                        <IconButton size="small" onClick={() => iniciarItem(item.uuid)} sx={{ color: tokens.palette.primary.main, p: 0.25 }}>
                                            <PlayArrow fontSize="small" />
                                        </IconButton>
                                    )}
                                    {ehDestaEstacao && item.status === StatusItemKDS.EM_PREPARO && (
                                        <>
                                            <Tooltip title="Desfazer">
                                                <IconButton size="small" onClick={() => reverterItem(item.uuid)} sx={{ color: tokens.textoFraco, p: 0.25 }}>
                                                    <Undo sx={{ fontSize: '0.9rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                            <IconButton size="small" onClick={() => concluirItem(item.uuid)} sx={{ color: tokens.palette.success.main, p: 0.25 }}>
                                                <CheckCircle fontSize="small" />
                                            </IconButton>
                                        </>
                                    )}
                                    {ehDestaEstacao && item.status === StatusItemKDS.PRONTO && (
                                        <Tooltip title="Desfazer">
                                            <IconButton size="small" onClick={() => reverterItem(item.uuid)} sx={{ color: tokens.palette.primary.main, p: 0.25 }}>
                                                <Undo sx={{ fontSize: '0.9rem' }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ width: '100vw', height: '100vh', background: tokens.painel, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ background: corEstacao, px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Voltar para configurações">
                        <IconButton size="small" onClick={() => navigate('/cozinha/configuracao')} sx={{ color: 'white' }}>
                            <ArrowBack fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="h5" fontWeight="bold" color="white">{nomeEstacao}</Typography>
                    <Chip label="Expedição por área" size="small" sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.7rem' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    {aguardandoDisparo.length > 0 && (
                        <Typography color="common.white" fontSize="0.9rem" fontWeight="bold">{aguardandoDisparo.length} aguardando disparo</Typography>
                    )}
                    <Typography color="white" fontSize="0.9rem">{emAndamento.length} em andamento</Typography>
                    <Typography color="white" fontSize="0.9rem">{prontos.length} prontos</Typography>
                    {loading && <CircularProgress size={20} sx={{ color: 'white' }} />}
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2, alignContent: 'flex-start', alignItems: 'start' }}>
                {pedidos.length === 0 && !loading && (
                    <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                        <Typography color="text.secondary" variant="h5">Nenhum pedido no momento</Typography>
                    </Box>
                )}
                {aguardandoDisparo.map(p => renderPedido(p, true))}
                {emDisparo.map(p => renderPedido(p, false))}
                {prontos.map(p => renderPedido(p, false))}
                {emAndamento.map(p => renderPedido(p, false))}
            </Box>
        </Box>
    );
};

// ─── Entrada: detecta modo ────────────────────────────────────────────────────

export const PainelEstacao: React.FC = () => {
    const { estacaoUuid } = useParams<{ estacaoUuid: string }>();
    const { estacoes } = useEstacoesKDS();
    const tokens = useKdsTokens();

    const estacao = estacoes.find(e => e.uuid === estacaoUuid);

    React.useEffect(() => {
        document.title = estacao?.nome ?? 'Estação KDS';
    }, [estacao?.nome]);

    // Sem cor cadastrada, a faixa da estação usa a cor da marca do cliente.
    const corEstacao = estacao?.cor ?? tokens.palette.primary.main;
    const nomeEstacao = estacao?.nome ?? 'Estação KDS';
    const tempoAlertaAmarelo = estacao?.configuracaoExibicao?.tempoAlertaAmareloSegundos ?? 300;
    const tempoAlertaVermelho = estacao?.configuracaoExibicao?.tempoAlertaVermelhoSegundos ?? 600;

    if (!estacaoUuid) return null;

    if (estacao?.modoExpedicaoArea) {
        return (
            <PainelExpedicaoArea
                estacaoUuid={estacaoUuid}
                nomeEstacao={nomeEstacao}
                corEstacao={corEstacao}
                tempoAlertaAmarelo={tempoAlertaAmarelo}
                tempoAlertaVermelho={tempoAlertaVermelho}
            />
        );
    }

    return (
        <PainelEstacaoNormal
            estacaoUuid={estacaoUuid}
            nomeEstacao={nomeEstacao}
            corEstacao={corEstacao}
            tempoAlertaAmarelo={tempoAlertaAmarelo}
            tempoAlertaVermelho={tempoAlertaVermelho}
        />
    );
};
